import { SORTABLE_DATE_FORMAT } from "modules/common/constants";
import { Milestone } from "./milestones-store";
import {
    ConflictMarkResponse,
    PlanAbsenceResponse,
    PlanDayOffWorkResponse,
    PlanSharedDayResponse,
} from "./plan-conflicts";
import { PlanLinkResponse } from "./plan-dependencies";
import { MakeMonthName } from "modules/common/models/period";
import { hex2rgba, shadeColor } from "modules/common/services/colors";
import moment from "moment";
import { PlanColumn, PlanPeriod } from "modules/spending/workload-plan/models/plan-types";
import {
    EMPTY_PROJECT_PLAN,
    PlanSegment,
    ProjectPaint,
    ProjectParticipant,
    ProjectPlanProject,
    PlanMilestone,
    ProjectPlanSectionView,
    ProjectPlanSnapshot,
    ProjectRowKind,
} from "./project-plan-types";

/** Прозрачность заливки полосы — та же, что в плановом табеле */
const BLOCK_ALPHA = 0.75;

/** На сколько затемнить цвет проекта для рамки */
const BORDER_SHADE = -30;

/** Ниже этой яркости подложки тёмная подпись сливается с заливкой */
const LIGHT_TEXT_THRESHOLD = 150;

const FALLBACK_COLOR = "#9b9b9b";

export interface ProjectPlanResponse {
    periods: ProjectPlanPeriodResponse[];
    orders: ProjectPlanOrderResponse[];
    /** Правка графика на все проекты */
    editAll?: boolean;
    /** Сотрудники, чьи работы пользователь двигает как начальник отдела */
    editableEmployers?: string[] | null;
    /** Плановые отсутствия сотрудников графика от сегодня — для конфликтов */
    absences?: PlanAbsenceResponse[] | null;
    /** Дни работ на выходных и праздниках от сегодня — для конфликтов */
    dayOffWorks?: PlanDayOffWorkResponse[] | null;
    /** Дни, поделённые между проектами в пределах нормы, — не перегрузка */
    sharedDays?: PlanSharedDayResponse[] | null;
    /** Связи с концом в проектах графика */
    links?: PlanLinkResponse[] | null;
    /** Пометки «Допустимо» на конфликтах */
    conflictMarks?: ConflictMarkResponse[] | null;
}

/** Хранимый плановый срок: проекта (contentGuid пуст) или раздела, yyyy-MM-dd */
export interface ProjectPlanTermResponse {
    contentGuid: string | null;
    start: string | null;
    end: string | null;
}

export interface ProjectPlanPeriodResponse {
    month: number;
    year: number;
    monthWorkDayCount: number;
    monthsWorkHours: number;
    /** Нормы по предварительному календарю (ТК РФ) */
    provisionalCalendar?: boolean;
    days: ProjectPlanDayResponse[];
}

export interface ProjectPlanDayResponse {
    day: string;
    dayOfMonth: string;
    isDayOff: boolean;
}

export interface ProjectPlanOrderResponse {
    id: string;
    name: string;
    color: string;
    inventoryNumber: number;
    fullInventoryNumber: string;
    orderNumber: string;
    /** Дата окончания по договору, yyyy-MM-dd */
    contractEnd?: string | null;
    status: string;
    statusColor: string;
    employer: string;
    employerId: string | null;
    lastDay: string;
    /** Полный размах работ по проекту, посчитанный сервером по всем участникам */
    span: ProjectPlanSegmentResponse | null;
    rows: ProjectPlanRowResponse[];
    sections: ProjectPlanSectionResponse[] | null;
    milestones: Milestone[] | null;
    canEditMilestones: boolean;
    terms?: ProjectPlanTermResponse[] | null;
}

export interface ProjectPlanSectionResponse {
    id: string;
    stage: string;
    code: string;
    name: string;
    description: string;
    note: string;
    outsourced: boolean;
    inactive: boolean;
    span: ProjectPlanSegmentResponse | null;
    rows: ProjectPlanRowResponse[];
}

export interface ProjectPlanSegmentResponse {
    /** Видимые границы, обрезанные бэком по запрошенному диапазону */
    from: string;
    to: string;
    /** Настоящий последний день отрезка */
    stopDay: string;
    /** Настоящая длина в рабочих днях */
    workDays: number;
    continuesLeft: boolean;
    continuesRight: boolean;
    /** Работа плана, за которую отрезок тянут на графике */
    assignmentId?: string | null;
    /** Прежнее положение работы из черновика — рисуется бледным контуром */
    ghost?: boolean;
    /** Предложенное положение из запроса на согласование (id запроса) — контур, тоже ghost */
    proposed?: string | null;
}

export interface ProjectPlanRowResponse {
    kind: ProjectRowKind;
    id: string;
    label: string;
    note: string;
    sectionId: string | null;
    segments: ProjectPlanSegmentResponse[];
}

/**
 * Разворачивает ответ бэка в модель страницы: непрерывную ленту рабочих дней
 * и отрезки в координатах колонок. Выходные и праздники в колонки не попадают.
 */
export function buildProjectPlan(
    response: ProjectPlanResponse | null,
    visibleMonths: number,
    today: moment.Moment = moment().startOf("day")
): ProjectPlanSnapshot {
    if (!response) {
        return EMPTY_PROJECT_PLAN;
    }

    const columns: PlanColumn[] = [];
    const periods: PlanPeriod[] = [];

    (response.periods || []).forEach((period, periodIndex) => {
        const firstColumn = columns.length;

        (period.days || []).forEach((day) => {
            if (day.isDayOff) {
                return;
            }

            const date = moment(day.day, SORTABLE_DATE_FORMAT);

            columns.push({
                day: day.day,
                dayOfMonth: day.dayOfMonth,
                month: period.month,
                year: period.year,
                periodIndex,
                isPast: date.isBefore(today),
                isToday: date.isSame(today, "day"),
                hidden: periodIndex >= visibleMonths,
            });
        });

        periods.push({
            month: period.month,
            year: period.year,
            name: MakeMonthName(period.month),
            workDaysCount: period.monthWorkDayCount,
            workHours: period.monthsWorkHours,
            provisional: !!period.provisionalCalendar,
            firstColumn,
            lastColumn: Math.max(firstColumn, columns.length - 1),
        });
    });

    // дни колонок отсортированы и сравнимы лексикографически (ISO) — этого хватает
    // и для точного попадания, и для отрезка, край которого пришёлся на выходной
    const days = columns.map((column) => column.day);

    // первая колонка, которую ещё можно менять: по ней работы режутся на прошлое и текущее
    const firstFuture = columns.findIndex((column) => !column.isPast);
    const cut = firstFuture < 0 ? columns.length : firstFuture;

    const projects = (response.orders || [])
        .map((order) => toProject(order, days, cut))
        .filter((project): project is ProjectPlanProject => project !== null)
        .map((project) => withTermColumns(project, days));

    return {
        columns,
        periods,
        projects,
        editAll: !!response.editAll,
        editableEmployers: response.editableEmployers || [],
    };
}

function toRows(rows: ProjectPlanRowResponse[] | null, days: string[], cut: number): ProjectParticipant[] {
    return (rows || [])
        .map((row) => ({
            kind: row.kind,
            id: row.id,
            label: row.label,
            note: row.note,
            sectionId: row.sectionId || null,
            segments: (row.segments || [])
                .map((segment) => toSegment(segment, days))
                .filter((segment): segment is PlanSegment => segment !== null)
                .flatMap((segment) => splitByToday(segment, cut, days))
                // прошлое у контура совпадает с самой работой — его не рисуем
                .filter((segment) => !(segment.ghost && segment.isPast)),
        }))
        .filter((row) => row.segments.length > 0);
}

/**
 * Работа плана, идущая через сегодняшний день, делится на две полосы: прошлую
 * (приглушена, не двигается) и текущую. Подрядчики — договор, их не режем.
 */
function splitByToday(segment: PlanSegment, cut: number, days: string[]): PlanSegment[] {
    return segment.assignmentId ? splitAt(segment, cut, days) : [segment];
}

/**
 * Полоса проекта или раздела на две части по сегодняшнему дню — для показа: прошлое
 * бледное и не двигается, как у работ. Сама полоса для расчётов остаётся целой.
 */
function partsOf(segment: PlanSegment | null, cut: number, days: string[]): PlanSegment[] {
    return segment ? splitAt(segment, cut, days) : [];
}

/**
 * Полоса проекта или раздела по работам: прошлая часть — от начала до последнего
 * прошедшего дня работ, текущая — от первого дня работ с сегодняшнего и до конца.
 * Текущая часть начинается там, где начинаются работы, а не с сегодняшнего дня:
 * сдвинули проект вправо — прошлое осталось на месте, а полоса уехала вместе с работами,
 * и между ними видно пустое место, а не растянутую полосу без работ.
 */
function workParts(summary: PlanSegment | null, rows: ProjectParticipant[], cut: number, days: string[]): PlanSegment[] {
    if (!summary) {
        return [];
    }

    const segments = rows.flatMap((row) => row.segments).filter((segment) => !segment.ghost);
    const end = summary.start + summary.length - 1;
    const parts: PlanSegment[] = [];

    const pastEnd = segments
        .filter((segment) => segment.start < cut)
        .reduce((result, segment) => Math.max(result, Math.min(segment.start + segment.length - 1, cut - 1)), -1);

    if (pastEnd >= summary.start) {
        parts.push({
            ...summary,
            length: pastEnd - summary.start + 1,
            workDays: pastEnd - summary.start + 1,
            stopDay: days[pastEnd],
            continuesRight: false,
            isPast: true,
        });
    }

    const futureStart = segments
        .filter((segment) => segment.start + segment.length - 1 >= cut)
        .reduce((result, segment) => Math.min(result, Math.max(segment.start, cut)), Number.MAX_SAFE_INTEGER);

    if (futureStart <= end) {
        parts.push({
            ...summary,
            start: futureStart,
            length: end - futureStart + 1,
            continuesLeft: summary.continuesLeft && futureStart === summary.start,
            isPast: false,
            // прошлое вплотную — левый край полосы не её начало, а сегодняшний день
            afterPast: futureStart === cut && pastEnd === cut - 1,
        });
    }

    return parts;
}

function splitAt(segment: PlanSegment, cut: number, days: string[]): PlanSegment[] {
    const end = segment.start + segment.length - 1;

    if (end < cut) {
        return [{ ...segment, isPast: true }];
    }

    if (segment.start >= cut) {
        return [segment];
    }

    const pastLength = cut - segment.start;

    return [
        {
            ...segment,
            length: pastLength,
            workDays: pastLength,
            stopDay: days[cut - 1],
            continuesRight: false,
            isPast: true,
        },
        {
            ...segment,
            start: cut,
            length: segment.length - pastLength,
            workDays: Math.max(0, segment.workDays - pastLength),
            continuesLeft: false,
            isPast: false,
            afterPast: true,
        },
    ];
}

/** Раздел остаётся, если на сетку попала его полоса или хоть один исполнитель */
function toSections(
    sections: ProjectPlanSectionResponse[] | null,
    days: string[],
    terms: ProjectPlanTermResponse[] | null | undefined,
    cut: number
): ProjectPlanSectionView[] {
    return (sections || [])
        .map((section) => ({
            id: section.id || "",
            stage: section.stage || "",
            code: section.code || "",
            name: section.name || "",
            description: section.description || "",
            note: section.note || "",
            outsourced: section.outsourced,
            inactive: section.inactive,
            summary: section.span ? toSegment(section.span, days) : null,
            term: termSegment(
                (terms || []).find((term) => !!section.id && term.contentGuid === section.id),
                days
            ),
            rows: toRows(section.rows, days, cut),
        }))
        // заданный срок — сплошной отрезок дат; полоса по работам — по самим работам
        .map((section) => ({
            ...section,
            parts: section.term
                ? partsOf(section.term, cut, days)
                : workParts(section.summary, section.rows, cut, days),
        }))
        .filter((section) => !!section.summary || !!section.term || section.rows.length > 0);
}

/** Заданный срок раздела на сетке; без начала или конца — не рисуется */
function termSegment(term: ProjectPlanTermResponse | undefined, days: string[]): PlanSegment | null {
    if (!term || !term.start || !term.end) {
        return null;
    }

    return toSegment(
        {
            from: term.start,
            to: term.end,
            stopDay: term.end,
            workDays: 0,
            continuesLeft: false,
            continuesRight: false,
        },
        days
    );
}

/**
 * Колонка, по правому краю которой стоит засечка планового срока: последний рабочий день
 * не позже срока. -1 — срок раньше сетки, days.length — позже; null — срок не задан.
 */
function planEndColumn(planEnd: string | null, days: string[]): number | null {
    if (!planEnd || !days.length) {
        return null;
    }

    return planEnd > days[days.length - 1] ? days.length : upperBound(days, planEnd);
}

function toProject(order: ProjectPlanOrderResponse, days: string[], cut: number): ProjectPlanProject | null {
    const rows = toRows(order.rows, days, cut);

    if (!rows.length) {
        return null;
    }

    return {
        id: order.id,
        name: order.name,
        color: order.color,
        inventoryNumber: order.inventoryNumber,
        fullInventoryNumber: order.fullInventoryNumber,
        orderNumber: order.orderNumber,
        status: order.status,
        statusColor: order.statusColor,
        employer: order.employer,
        employerId: order.employerId || null,
        lastDay: order.lastDay,
        paint: paintOf(order.color),
        rows,
        sections: toSections(order.sections, days, order.terms, cut),
        milestones: toMilestones(order.milestones, days),
        canEditMilestones: !!order.canEditMilestones,
        summary: order.span ? toSegment(order.span, days) : null,
        summaryParts: workParts(order.span ? toSegment(order.span, days) : null, rows, cut, days),
        ...toTerms(order.terms),
        planEndColumn: null,
    };
}

/** Сроки проекта и разделов: у раздела без записи срок равен его работам */
function toTerms(terms: ProjectPlanTermResponse[] | null | undefined) {
    const sectionTerms: { [guid: string]: { start: string | null; end: string | null } } = {};
    let planEnd: string | null = null;

    (terms || []).forEach((term) => {
        if (term.contentGuid) {
            sectionTerms[term.contentGuid] = { start: term.start, end: term.end };
        } else {
            planEnd = term.end;
        }
    });

    return { planEnd, sectionTerms };
}

/** Засечки сроков — после сборки, когда известны колонки */
function withTermColumns(project: ProjectPlanProject, days: string[]): ProjectPlanProject {
    return { ...project, planEndColumn: planEndColumn(project.planEnd, days) };
}

function toMilestones(milestones: Milestone[] | null, days: string[]): PlanMilestone[] {
    return (milestones || []).map((milestone) => ({
        ...milestone,
        // раньше первой колонки — -1: веха за левым краем шкалы
        column: days.length && milestone.day < days[0] ? -1 : lowerBound(days, milestone.day),
    }));
}

/** Отрезок дат → диапазон колонок. null, если в диапазон не попало ни одного рабочего дня */
function toSegment(segment: ProjectPlanSegmentResponse, days: string[]): PlanSegment | null {
    if (!segment.from || !segment.to || !days.length) {
        return null;
    }

    const start = lowerBound(days, segment.from);
    const end = upperBound(days, segment.to);

    if (start > end) {
        return null;
    }

    return {
        start,
        length: end - start + 1,
        workDays: segment.workDays,
        stopDay: segment.stopDay,
        // отрезки из черновика приходят необрезанными — край за сеткой видно по датам
        continuesLeft: segment.continuesLeft || segment.from < days[0],
        continuesRight: segment.continuesRight || segment.to > days[days.length - 1],
        assignmentId: segment.assignmentId || null,
        ghost: !!segment.ghost,
        proposed: segment.proposed || null,
        isPast: false,
    };
}

/** Индекс первого дня, который не раньше value; days.length, если такого нет */
function lowerBound(days: string[], value: string): number {
    let low = 0;
    let high = days.length;

    while (low < high) {
        const middle = (low + high) >> 1;

        if (days[middle] < value) {
            low = middle + 1;
        } else {
            high = middle;
        }
    }

    return low;
}

/** Индекс последнего дня, который не позже value; -1, если такого нет */
function upperBound(days: string[], value: string): number {
    let low = 0;
    let high = days.length;

    while (low < high) {
        const middle = (low + high) >> 1;

        if (days[middle] <= value) {
            low = middle + 1;
        } else {
            high = middle;
        }
    }

    return low - 1;
}

function paintOf(color: string): ProjectPaint {
    const normalized = /^#[0-9a-f]{6}$/i.test(color) ? color : FALLBACK_COLOR;

    return {
        background: hex2rgba(normalized, BLOCK_ALPHA),
        borderColor: shadeColor(normalized, BORDER_SHADE),
        // полоса полупрозрачная и лежит на белом — контраст считаем по смешанному цвету
        dark: luminance(normalized, BLOCK_ALPHA) < LIGHT_TEXT_THRESHOLD,
        chipBackground: normalized,
        // плашка залита чистым цветом проекта — у неё своя яркость
        chipDark: luminance(normalized, 1) < LIGHT_TEXT_THRESHOLD,
        // смешиванием, а не shadeColor: тот умножает каналы, и почти чёрный цвет им не осветлить
        milestoneFill:
            luminance(normalized, 1) < LIGHT_TEXT_THRESHOLD
                ? mixColor(normalized, 255, MILESTONE_LIGHTEN)
                : mixColor(normalized, 0, MILESTONE_DARKEN),
    };
}

/** На сколько осветлить веху тёмного проекта и затемнить веху светлого (доля смеси) */
const MILESTONE_LIGHTEN = 0.5;
const MILESTONE_DARKEN = 0.35;

/** Смешать цвет с белым (target 255) или чёрным (target 0) в доле ratio */
function mixColor(color: string, target: number, ratio: number): string {
    const value = parseInt(color.slice(1), 16);
    const channel = (shift: number) => {
        const source = (value >> shift) & 255;
        return Math.round(source + (target - source) * ratio)
            .toString(16)
            .padStart(2, "0");
    };

    return `#${channel(16)}${channel(8)}${channel(0)}`;
}

/**
 * Воспринимаемая яркость цвета, смешанного с белым фоном сетки при заданной
 * непрозрачности. По ней и выбирается цвет подписи: тёмный фон — светлый текст,
 * светлый фон — тёмный.
 */
function luminance(color: string, alpha: number): number {
    const value = parseInt(color.slice(1), 16);
    const mix = (channel: number) => channel * alpha + 255 * (1 - alpha);

    const r = mix((value >> 16) & 255);
    const g = mix((value >> 8) & 255);
    const b = mix(value & 255);

    return 0.299 * r + 0.587 * g + 0.114 * b;
}
